// components/Testimonials.jsx
import React, { useState, useEffect } from 'react';
import { footerBannersApi } from '../../api/testinomials';

const Testimonials = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  // Check screen size for mobile view
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch banners from API
  useEffect(() => {
    const fetchBanners = async () => {
      try {
        setLoading(true);
        const data = await footerBannersApi.getFooterBanners();
        setBanners(data);
        setError(null);
      } catch (err) {
        setError(err.message || 'Failed to fetch testimonials');
        console.error('Error fetching banners:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchBanners();
  }, []);

  // Auto-slide for mobile carousel
  useEffect(() => {
    if (isMobile && banners.length > 1) {
      const interval = setInterval(() => {
        nextSlide();
      }, 5000); // Change slide every 5 seconds
      
      return () => clearInterval(interval);
    }
  }, [isMobile, banners.length, currentIndex]);

  const nextSlide = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((prevIndex) => 
      prevIndex === banners.length - 1 ? 0 : prevIndex + 1
    );
    setTimeout(() => setIsAnimating(false), 500);
  };

  const prevSlide = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? banners.length - 1 : prevIndex - 1
    );
    setTimeout(() => setIsAnimating(false), 500);
  };

  const goToSlide = (index) => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex(index);
    setTimeout(() => setIsAnimating(false), 500);
  };

  const getImageUrl = (imgPath) => footerBannersApi.getImageUrl(imgPath);

  const handleRetry = () => {
    setError(null);
    setLoading(true);
    footerBannersApi.getFooterBanners()
      .then(data => {
        setBanners(data);
        setError(null);
      })
      .catch(err => {
        setError(err.message || 'Failed to fetch testimonials');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-[400px] flex flex-col items-center justify-center bg-gray-50">
        <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        <p className="mt-4 text-gray-600 font-medium">Loading testimonials...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-[400px] flex items-center justify-center bg-gray-50 px-4">
        <div className="bg-red-100 border border-red-400 text-red-700 px-6 py-4 rounded-lg text-center max-w-md">
          <p className="font-semibold">Error: {error}</p>
          <button 
            onClick={handleRetry}
            className="mt-2 bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Empty state
  if (banners.length === 0) {
    return (
      <div className="min-h-[400px] flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <p className="text-gray-500 text-lg">No testimonials found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 p-8">
    
      
      {/* Mobile View - Carousel */}
      {isMobile && (
        <div className="relative md:hidden">
          {/* Carousel Container */}
          <div className="overflow-hidden rounded-xl shadow-lg">
            <div 
              className="flex transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${currentIndex * 100}%)` }}
            >
              {banners.map((banner) => (
                <div 
                  key={banner._id} 
                  className="w-full flex-shrink-0"
                >
                  <div className="bg-white rounded-xl overflow-hidden">
                    <div className="relative w-full h-[400px] bg-gray-200">
                      <img
                        src={getImageUrl(banner.img)}
                        alt={banner.description || 'Testimonial image'}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/400x400?text=No+Image';
                          e.target.onerror = null;
                        }}
                      />
                    </div>
                 
                  </div>
                </div>
              ))}
            </div>
          </div>

   
        </div>
      )}

      {/* Web/Tablet View - Responsive Grid */}
      {!isMobile && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
          {banners.map((banner) => (
            <div 
              key={banner._id} 
              className="bg-white rounded-xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl"
            >
              <div className="relative w-full h-[300px] md:h-[350px] lg:h-[400px] overflow-hidden bg-gray-100">
                <img
                  src={getImageUrl(banner.img)}
                  alt={banner.description || 'Testimonial image'}
                  className="w-full h-full object-cover transition-transform duration-300"
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/400x400?text=No+Image';
                    e.target.onerror = null;
                  }}
                />
              </div>
            
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Testimonials;